import Select, { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { faAngleDown, faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { InlineTooltip } from 'apps/builder/components/settings/modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const modernSelectClasses = {
  container: () => 'foundry--exposed-dropdown foundry--react-select',
  control: () =>
    'dropdown-input__wrapper foundry--input-fields modern--input non-input--wrapper',
  menu: () => 'dropdown-menu__wrapper foundry--surface',
  option: () => 'dropdown--option',
  indicatorsContainer: () => 'input-fields__icon-container',
}

const modernTheme = theme => ({
  borderRadius: 'var(--foundry-modern--border-radius)',
  colors: {
    ...theme.colors,
    primary: 'var(--foundry-color-theme-primary)',
    neutral20: 'var(--foundry-modern--border-color)',
    neutral30: 'var(--foundry-modern--border-color)',
  },
  spacing: {
    baseUnit: '0',
    controlHeight: 'calc(1em + (var(--foundry-modern--padding-block) * 2))',
  },
})

const modernStyles = {
  menu: (baseStyles, { selectProps }) => {
    return selectProps.menuIsOpen
      ? {
          display: 'block !important',
        }
      : {}
  },
  option: (baseStyles, { selectProps }) => {
    return {
      ...baseStyles,
      padding:
        'var(--foundry-modern--padding-inline) var(--foundry-modern--padding-block)',
    }
  },
  control: ({ alignItems, outline, ...baseStyles }, { selectProps }) => {
    return {
      ...baseStyles,
      paddingBlock: 'var(--foundry-modern--padding-block)',
      paddingInline: 'var(--foundry-modern--padding-inline)',
      boxShadow: 'var(--foundry-modern--shadow)',
    }
  },
  indicatorsContainer: ({ padding, ...baseStyles }, { selectProps }) => {
    return {
      ...baseStyles,
      '--foundry-input--background-color': 'transparent',
      color: 'var(--foundry-input--color)',
    }
  },
}

const ModernSelectInput = ({ ...props }) => {
  return <components.Input {...props} inputClassName={'input-fields__input'} />
}

const ModernMenuList = ({ children }) => <>{children}</>

export const SingleValue = ({ innerProps, data }) => {
  const { label, component: Component, ...rest } = data
  return (
    <div className={`dropdown--value clipped-text`} {...innerProps}>
      {Component ? <Component label={label} {...rest} /> : label}
    </div>
  )
}

const DropdownIndicator = () => (
  <div className="dropdown--indicator foundry-icon foundry-icon--svg">
    <FontAwesomeIcon icon={faAngleDown} />
  </div>
)

export const ModernOption = ({
  children,
  data,
  innerProps,
  innerRef,
  isDisabled,
  isFocused,
  isSelected,
}) => {
  const { component: Component } = data
  return (
    <div
      className={`dropdown--option 
        ${isSelected ? 'option--selected' : ''} 
        ${isFocused ? 'option--focused' : ''} 
        ${isDisabled ? 'option--label--disabled' : ''}`}
      {...innerProps}
      ref={innerRef}
      aria-disabled={isDisabled}
    >
      {Component ? <Component {...data} /> : children}
    </div>
  )
}

export const ModernOptionLabelWithTooltip = ({
  label,
  isDisabled,
  tooltipText,
}) => (
  <div className="dropdown--option--label-with-tooltip">
    <span
      className={
        isDisabled ? 'option--label--disabled' : 'option--label--enabled'
      }
    >
      {label}
    </span>
    {isDisabled && tooltipText && (
      <InlineTooltip
        tooltip={tooltipText}
        icon={faInfoCircle}
        direction="right"
      />
    )}
  </div>
)

export const ModernSelect = ({
  options,
  components,
  onChange,
  value,
  isDisabled,
  additionalProps = {},
  createable = false,
  id: inputId,
}) => {
  const SelectComponent = createable ? CreatableSelect : Select
  return (
    <SelectComponent
      components={{
        Input: ModernSelectInput,
        MenuList: ModernMenuList,
        Option: ModernOption,
        DropdownIndicator,
        SingleValue,
        IndicatorSeparator: null,
        ...components,
      }}
      inputId={inputId}
      classNamePrefix="react-select"
      classNames={modernSelectClasses}
      theme={modernTheme}
      styles={modernStyles}
      options={options}
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
      {...additionalProps}
    />
  )
}
